/* eslint-disable max-lines, react/jsx-no-bind */
import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from '@utils/i18n/i18n';

import Button from '@App/components/Button/Button';
import QuestionNextButton from '@App/components/QuestionNextButton/QuestionNextButton';
import AnswerRanking from '@App/components/AnswerRanking/AnswerRanking';
import Keyboard from '@App/components/Keyboard/Keyboard';
import Search from '@App/components/Search/Search';
import QuestionProgress from '@App/components/QuestionProgress/QuestionProgress';
import theme from '@style/theme.json';
import formVisuals from '@App/components/QuestionFormVisuals/question-form-visuals';
import { municipalityOptions, resolveResidenceCoordinates } from './municipality-data';
import '@App/pages/Tablet/Question1/question1-form.css';

function Question2Form() {
  const { onSelectAnswer } = useOutletContext();
  const { t } = useTranslation();
  const { tabletId } = useParams();
  const isTablet = Boolean(tabletId);
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const visual = formVisuals.question2;
  const inputQuery = inputValue.trim().toLocaleLowerCase('nl-BE');
  const options = municipalityOptions.filter((option) => {
    const labelMatches = option.label.toLocaleLowerCase('nl-BE').includes(inputQuery);
    const postcodeMatches = option.sublabel?.includes(inputValue.trim());

    return labelMatches || postcodeMatches;
  });

  useEffect(() => {
    setSelectedValue(current => (current && current.label === inputValue ? current : null));
  }, [inputValue]);

  async function submitAnswer() {
    const residence = sessionStorage.getItem('tabletResidence');
    const storedCoordinates = sessionStorage.getItem('tabletResidenceCoordinates');
    const startCoordinates = storedCoordinates
      ? JSON.parse(storedCoordinates)
      : await resolveResidenceCoordinates(residence);

    onSelectAnswer('question2', { ...selectedValue, startCoordinates, residence });
    setAnswerSubmitted(true);
    setIsKeyboardOpen(false);
  }

  function handleInputFocus() { setIsKeyboardOpen(true); }
  function handleInputPointerDown() { setIsKeyboardOpen(true); }
  function handleInputChange(event) { setInputValue(event.target.value); setSelectedValue(null); }
  function handleSelectMunicipality(item) { setSelectedValue(item); }
  function handleKeyboardClose() { setIsKeyboardOpen(false); setKeyboardHeight(0); }

  const inputRootProps = { 'data-keyboard-target': true, onFocus: handleInputFocus, onPointerDown: handleInputPointerDown };

  return (
    <div className={`question1-form question1-form--question2${isTablet && isKeyboardOpen ? ' question1-form--keyboard-open' : ''}`} style={{ '--form-accent': visual.accent, '--form-accent-soft': visual.accentSoft, '--keyboard-height': `${keyboardHeight || 196}px` }}>
      <div className="question1-form__body">
        <QuestionProgress questionId="question2" />
        {!answerSubmitted ? (
          <>
            <h2 className="question1-form__body__title">
              <span>{t('relocation-title')}</span>
            </h2>
            <Search
              value={inputValue}
              rootElProps={inputRootProps}
              onInputChange={handleInputChange}
              onSelect={handleSelectMunicipality}
              options={options}
              selectedValue={selectedValue}
              placeholder={t('relocation-input-placeholder')}
              resultsTitle={t('relocation-results-title')}
              color={Number(tabletId) === 1
                ? theme?.color?.primary?.main
                : theme?.color?.secondary?.main}
            />
            <div className="question1-form__body__footer">
              <Button disabled={!selectedValue} onClick={submitAnswer}>
                {t('arc-layer-submit-button')}
              </Button>
            </div>
          </>
        ) : (
          <div className="question1-form__thank-you">
            <h2 className="question1-form__body__title">
              <span>{t('arc-layer-thank-you')}</span>
            </h2>
            <p className="question1-form__thank-you-text">{t('relocation-thank-you-text')}</p>
            <AnswerRanking questionId="question2" selectedAnswer={selectedValue} />
            <div className="question1-form__body__footer">
              <QuestionNextButton onNext={() => navigate('../question3')}>
                {t('relocation-next-button')}
              </QuestionNextButton>
            </div>
          </div>
        )}
      </div>
      {isTablet && (
        <Keyboard
          isOpen={isKeyboardOpen}
          onClose={handleKeyboardClose}
          onHeightChange={setKeyboardHeight}
          value={inputValue}
          onChange={setInputValue}
        />
      )}
    </div>
  );
}

export default Question2Form;
